// The farmer: position in tile space (y grows downward, like the map array),
// collision against solid tiles, and animation frame selection from the pack.
import { PLAYER_RADIUS, PLAYER_SPEED } from "./config.ts";
import type { World } from "./world.ts";

export type Dir = "down" | "left" | "right" | "up";

const DIR_VECTORS: Record<Dir, { x: number; y: number }> = {
  down: { x: 0, y: 1 },
  up: { x: 0, y: -1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
};

/** Poses that hold rather than play out: fishing, hauling, collapsing. */
export type Stance = "normal" | "carry" | "fish" | "reel" | "faint";

export class Player {
  x: number;
  y: number;
  dir: Dir = "down";
  moving = false;
  stance: Stance = "normal";
  /** slows the walk while the satchel is heavy */
  speedScale = 1;
  private animTime = 0;
  private action: { anim: string; time: number; duration: number; frames: number } | null = null;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  get busy(): boolean {
    return this.action !== null;
  }

  /** Fraction through the current swing, 0..1. */
  get actionProgress(): number {
    return this.action ? this.action.time / this.action.duration : 0;
  }

  /** The animation currently playing out, if any. */
  get actionAnim(): string | null {
    return this.action?.anim ?? null;
  }

  startAction(anim: string, duration: number, frames = 3): void {
    this.action = { anim, time: 0, duration, frames };
  }

  cancelAction(): void {
    this.action = null;
  }

  face(dir: Dir): void {
    this.dir = dir;
  }

  /** Turn to look at a tile — used when a click targets something off-axis. */
  faceTile(tx: number, ty: number): void {
    const dx = tx + 0.5 - this.x;
    const dy = ty + 0.5 - this.y;
    if (Math.abs(dx) > Math.abs(dy)) this.dir = dx > 0 ? "right" : "left";
    else this.dir = dy > 0 ? "down" : "up";
  }

  facingTile(): { x: number; y: number } {
    const v = DIR_VECTORS[this.dir];
    return {
      x: Math.floor(this.x + v.x * 0.85),
      y: Math.floor(this.y + v.y * 0.85),
    };
  }

  tile(): { x: number; y: number } {
    return { x: Math.floor(this.x), y: Math.floor(this.y) };
  }

  /** Tiles a tool at this tier reaches, centred on the faced tile. */
  areaTiles(area: 1 | 3 | 9): { x: number; y: number }[] {
    const t = this.facingTile();
    if (area === 1) return [t];
    if (area === 9) {
      const out: { x: number; y: number }[] = [];
      for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) out.push({ x: t.x + dx, y: t.y + dy });
      return out;
    }
    // a row of three, laid across the direction of the swing
    const across = this.dir === "left" || this.dir === "right" ? { x: 0, y: 1 } : { x: 1, y: 0 };
    return [
      { x: t.x - across.x, y: t.y - across.y },
      t,
      { x: t.x + across.x, y: t.y + across.y },
    ];
  }

  update(dt: number, move: { x: number; y: number }, world: World): void {
    this.animTime += dt;

    if (this.stance === "faint") {
      this.moving = false;
      return;
    }

    if (this.action) {
      this.action.time += dt;
      if (this.action.time >= this.action.duration) this.action = null;
      this.moving = false;
      return;
    }

    let dx = move.x;
    let dy = move.y;
    const len = Math.hypot(dx, dy);
    this.moving = len > 0;
    if (!this.moving) return;

    dx /= len;
    dy /= len;
    // face along the dominant axis, preferring the newly pressed one
    if (Math.abs(dx) > Math.abs(dy)) this.dir = dx > 0 ? "right" : "left";
    else this.dir = dy > 0 ? "down" : "up";

    const step = PLAYER_SPEED * this.speedScale * dt;
    this.moveAxis(dx * step, 0, world);
    this.moveAxis(0, dy * step, world);
  }

  private moveAxis(dx: number, dy: number, world: World): void {
    if (dx === 0 && dy === 0) return;
    const nx = this.x + dx;
    const ny = this.y + dy;
    if (!this.collides(nx, ny, world)) {
      this.x = nx;
      this.y = ny;
      return;
    }
    // slide along the blocked axis at reduced speed so corners are not sticky
    const half = 0.5;
    if (!this.collides(this.x + dx * half, this.y + dy * half, world)) {
      this.x += dx * half;
      this.y += dy * half;
    }
  }

  private collides(px: number, py: number, world: World): boolean {
    const r = PLAYER_RADIUS;
    const x0 = Math.floor(px - r);
    const x1 = Math.floor(px + r);
    const y0 = Math.floor(py - r);
    const y1 = Math.floor(py + r);
    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        if (world.blocked(x, y)) return true;
      }
    }
    return false;
  }

  /** Atlas sprite id for the current frame. */
  spriteId(): string {
    if (this.stance === "faint") return "farmer_faint";
    if (this.action) {
      const frames = this.action.frames;
      const i = Math.min(frames - 1, Math.floor(this.actionProgress * frames));
      return `${this.action.anim}_${this.dir}_${i}`;
    }
    if (this.stance === "reel") {
      return `farmer_reel_${this.dir}_${Math.floor(this.animTime * 7) % 2}`;
    }
    if (this.stance === "fish") {
      return `fish_${this.dir}_${Math.floor(this.animTime * 2) % 2}`;
    }
    if (this.stance === "carry") {
      const i = this.moving ? Math.floor(this.animTime * 6) % 2 : 0;
      return `carry_${this.dir}_${i}`;
    }
    if (this.moving) {
      const i = Math.floor(this.animTime * 9) % 4;
      return `walk_${this.dir}_${i}`;
    }
    const i = Math.floor(this.animTime * 2.2) % 2;
    return `idle_${this.dir}_${i}`;
  }
}
